import { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import { callTool } from '../lib/tools'
import { useOrgRole } from '../hooks/useOrgRole'
import { bareInput } from '../utilities/formAlign'
import {
  GRANT_HOURS,
  supportAccessFrom,
  supportEndArgs,
  supportGrantArgs,
  supportReadArgs,
  supportRefusal,
  supportSettingArgs,
  supportStatusLine,
  type SupportAccess,
} from '../utilities/supportAccess'

type Props = { orgId: string }

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

/**
 * "Allow noBGP support" and the temporary support grant (app#376, router `org_support_access`,
 * router#1772; docs/PERMISSIONS.md § 8, decided F-11).
 *
 * Every member reads it; an org Owner or Admin changes it (`support.manage`). The router gates each
 * op the same way, and has no staff arm on this tool: staff cannot let themselves in.
 *
 * ⚠ The state is read from the ROUTER, never from `organizations` through PostgREST. The columns
 * come with router migration 0225, and a select that named them before the apply would fail.
 * ⚠ A failed read renders NOTHING rather than a guess: "off" would tell the reader staff are locked
 * out when they may not be, and "on" the reverse. A router before #1772 answers 404 here.
 */
export default function OrgSupportAccess({ orgId }: Readonly<Props>) {
  const { can } = useOrgRole()
  const canManage = can('support.manage')
  const [access, setAccess] = useState<SupportAccess | null>(null)
  const [hours, setHours] = useState(24)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setAccess(null)
    setError(null)
    try {
      setAccess(supportAccessFrom(await callTool('org_support_access', supportReadArgs(orgId))))
    } catch (e) {
      console.warn('org_support_access get failed:', e)
    }
  }, [orgId])

  useEffect(() => {
    void load()
  }, [load])

  // Every change answers with the state after it, so the reply is what the page shows.
  const change = async (args: Record<string, unknown>) => {
    setBusy(true)
    setError(null)
    try {
      const next = supportAccessFrom(await callTool('org_support_access', args))
      if (next) setAccess(next)
      else await load()
    } catch (e) {
      console.error('org_support_access failed:', e)
      setError(supportRefusal(e))
    } finally {
      setBusy(false)
    }
  }

  if (!access) return null

  const grantLive = !!access.grantUntil && new Date(access.grantUntil).getTime() > Date.now()

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
        noBGP support
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={access.allowSupport}
            disabled={!canManage || busy}
            onChange={e => void change(supportSettingArgs(orgId, e.target.checked))}
          />
        }
        label="Allow noBGP support"
      />
      <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
        {supportStatusLine(access, Date.now(), formatTime)}
      </Typography>
      {/* The temporary grant matters only while the setting is off; a live one is kept when the
          setting changes, so it can still be ended here. */}
      {canManage && (!access.allowSupport || grantLive) && (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
          {!access.allowSupport && (
            <>
              <Select
                size="small"
                value={hours}
                onChange={e => setHours(Number(e.target.value))}
                disabled={busy}
                sx={bareInput}
              >
                {GRANT_HOURS.map(g => (
                  <MenuItem key={g.hours} value={g.hours}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="outlined"
                size="small"
                disabled={busy}
                onClick={() => void change(supportGrantArgs(orgId, hours))}
              >
                {grantLive ? 'Replace temporary access' : 'Give temporary access'}
              </Button>
            </>
          )}
          {grantLive && (
            <Button variant="outlined" color="error" size="small" disabled={busy} onClick={() => void change(supportEndArgs(orgId))}>
              End temporary access now
            </Button>
          )}
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}
